import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StudentTermReportResponseDto } from "../../shared/dtos/studentTermReport/StudentTermReportResponseDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";

export interface StudentTermReportState {
  // Leaderboard / Reports List
  loadingReports: boolean;
  errorLoadingReports: string | null;
  reports: StudentTermReportResponseDto[];

  // Single Report Detail View
  loadingReport: boolean;
  errorLoadingReport: string | null;
  report: StudentTermReportResponseDto | null;

  // Batch Generation Engine
  loadingGeneration: boolean;
  errorGenerating: string | null;

  // Remarks / Updates
  loadingUpdate: boolean;
  errorUpdating: string | null;

  // Email / SMS Background Dispatch
  loadingDispatch: boolean;
  errorDispatching: string | null;

  // Parent Portal Publish Toggle
  loadingPublish: boolean;
  errorPublish: string | null;

  pagination: PaginationDto | null;

  // Server Communication Status
  serverStatus: ServerStatusPayload | null;
}

const initialState: StudentTermReportState = {
  loadingReports: false,
  errorLoadingReports: null,
  reports: [],

  loadingReport: false,
  errorLoadingReport: null,
  report: null,

  loadingGeneration: false,
  errorGenerating: null,

  loadingUpdate: false,
  errorUpdating: null,

  loadingDispatch: false,
  errorDispatching: null,

  loadingPublish: false,
  errorPublish: null,

  pagination: null,

  serverStatus: null,
};

export const studentTermReportSlice = createSlice({
  name: "studentTermReport",
  initialState,
  reducers: {
    // 1. REPORTS LIST (LEADERBOARD)
    setLoadingReports: (state) => {
      state.loadingReports = true;
      state.errorLoadingReports = null;
    },
    setReports: (
      state,
      action: PayloadAction<StudentTermReportResponseDto[]>,
    ) => {
      state.loadingReports = false;
      state.reports = action.payload;
      state.errorLoadingReports = null;
    },
    setErrorLoadingReports: (state, action: PayloadAction<string | null>) => {
      state.loadingReports = false;
      state.errorLoadingReports = action.payload;
    },

    // 2. SINGLE REPORT DETAILS
    setLoadingReport: (state) => {
      state.loadingReport = true;
      state.errorLoadingReport = null;
    },
    setReport: (state, action: PayloadAction<StudentTermReportResponseDto>) => {
      state.loadingReport = false;
      state.report = action.payload;
      state.errorLoadingReport = null;
    },
    setErrorLoadingReport: (state, action: PayloadAction<string | null>) => {
      state.loadingReport = false;
      state.errorLoadingReport = action.payload;
    },

    // 3. GENERATION ENGINE
    setLoadingGeneration: (state) => {
      state.loadingGeneration = true;
      state.errorGenerating = null;
    },
    generateReportsSuccess: (state) => {
      state.loadingGeneration = false;
      state.errorGenerating = null;
    },
    setErrorGenerating: (state, action: PayloadAction<string | null>) => {
      state.loadingGeneration = false;
      state.errorGenerating = action.payload;
    },

    // 4. UPDATE REMARKS / STATUS
    setLoadingUpdate: (state) => {
      state.loadingUpdate = true;
      state.errorUpdating = null;
    },
    updateReportSuccess: (
      state,
      action: PayloadAction<StudentTermReportResponseDto>,
    ) => {
      state.loadingUpdate = false;
      state.errorUpdating = null;
      state.reports = state.reports.map((r) =>
        r.id === action.payload.id ? action.payload : r,
      );
      if (state.report && state.report.id === action.payload.id) {
        state.report = action.payload;
      }
    },
    setErrorUpdating: (state, action: PayloadAction<string | null>) => {
      state.loadingUpdate = false;
      state.errorUpdating = action.payload;
    },

    // 5. DISPATCH BACKGROUND TASK
    setLoadingDispatch: (state) => {
      state.loadingDispatch = true;
      state.errorDispatching = null;
    },
    dispatchReportsSuccess: (state) => {
      state.loadingDispatch = false;
      state.errorDispatching = null;
    },
    setErrorDispatching: (state, action: PayloadAction<string | null>) => {
      state.loadingDispatch = false;
      state.errorDispatching = action.payload;
    },

    // 6. PUBLISH TOGGLE
    setLoadingPublish: (state) => {
      state.loadingPublish = true;
      state.errorPublish = null;
    },
    publishReportsSuccess: (
      state,
      action: PayloadAction<{ publish: boolean }>,
    ) => {
      state.loadingPublish = false;
      state.errorPublish = null;
      state.reports = state.reports.map((r) => ({
        ...r,
        isPublished: action.payload.publish,
      }));
      if (state.report) {
        state.report.isPublished = action.payload.publish;
      }
    },
    setErrorPublish: (state, action: PayloadAction<string | null>) => {
      state.loadingPublish = false;
      state.errorPublish = action.payload;
    },

    setPagination: (state, action: PayloadAction<PaginationDto | null>) => {
      state.pagination = action.payload;
    },

    // 7. SERVER STATUS HANDLER
    setServerResponseStatus: (
      state,
      action: PayloadAction<ServerStatusPayload | null>,
    ) => {
      state.serverStatus = action.payload;
    },
  },
});

export const studentTermReportActions = studentTermReportSlice.actions;
export default studentTermReportSlice.reducer;
